import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TsButtonModule, TSIconModule } from 'tsui';

import { TaskApprovalComponent } from '../../tasks/task-details/task-approval/task-approval.component';
import { Task, Todo } from '@tasks/tasks.types';
import { ApprovalFooterComponent } from '../approval-footer/approval-footer.component';
import { Approval } from '../approvals.types';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';

@Component({
  selector: 'task-approval-page',
  standalone: true,
  imports: [CommonModule, TaskApprovalComponent, TSIconModule, TsButtonModule, ApprovalFooterComponent, RouterModule],
  template: `
    <div class="task-approval-page px-4 pt-12" *ngIf="approval">
      @if (!!task.project || !!task.property) {
        <a [routerLink]="projectOrPropertyLink()">
          {{ task.project?.title || task.property?.title }}
        </a>

        @if (task.task_list_name) {
          <span> - {{ task.task_list_name }}</span>
        }
      }

      <h1 class="task-approval-page__title mt-1">
        <a class="underline" [href]="approval?.approvable_url" target="_blank" rel="noopener">{{ task.title }}</a>
      </h1>
      <div class="task-approval-page__details">
        <div class="task-approval-page__details-field">
          <div class="label">Assign to</div>
          <div class="value">
            {{ task.assigned_user_name }}
          </div>
        </div>

        <div class="task-approval-page__details-field" *ngIf="!!task.contacts?.length">
          <div class="label">Contacts</div>
          <div class="tag-list">
            <div class="tag" *ngFor="let contact of task.contacts">{{ contact.name }}</div>
          </div>
        </div>

        <div class="task-approval-page__details-field" *ngIf="task.approvals">
          <div class="label">Approval status</div>
          <task-approval label="" [viewMode]="true" [approvals]="task.approvals"></task-approval>
        </div>

        <div class="task-approval-page__details-field">
          <div class="label">Due date</div>
          <div class="value">{{ task.due_date | date: 'MMM dd, y, hh:mm aaa' }}</div>
        </div>

        <div class="task-approval-page__details-field" *ngIf="task.description">
          <div class="label">Description</div>
          <div class="value">
            {{ task.description }}
          </div>
        </div>

        <div class="task-approval-page__details-field" *ngIf="todos?.length">
          <div class="label">Checklist</div>
          <div class="value">
            <ul>
              <li class="flex gap-1 mb-2" *ngFor="let todo of todos">
                <ts-icon [icon]="'check_box_outline_blank'" *ngIf="!todo.done"></ts-icon>
                <ts-icon [icon]="'check_box_outline'" *ngIf="todo.done"></ts-icon>
                {{ todo.title }}
              </li>
            </ul>
          </div>
        </div>

        <div class="task-approval-page__details-field" *ngIf="task.file_resources?.length">
          <div class="label">Files</div>
          <div class="value">
            <ul>
              <li class="flex gap-1 mb-2" *ngFor="let file of task.file_resources">
                <ts-icon [icon]="'description'"></ts-icon>
                <a class="underline" [href]="file.document_url" target="_blank"> {{ file.name }} </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <approval-footer [approval]="approval"></approval-footer>
  `,
  styleUrls: ['./task-approval-page.component.scss'],
})
export class TaskApprovalPageComponent extends OnDestroyMixin implements OnInit {
  approval: Approval;
  task: Task;

  constructor(private activatedRoute: ActivatedRoute) {
    super();
  }

  ngOnInit(): void {
    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe(({ approval }) => {
      this.approval = approval;
      this.task = this.approval.related_to as Task;
    });
  }

  projectOrPropertyLink(): string[] {
    if (this.task.project) {
      return ['/projects', this.task.project.pipeline_id.toString(), this.task.project.id.toString(), 'overview'];
    } else if (this.task.property) {
      return ['/properties', this.task.property?.id.toString()];
    }
  }

  get todos(): Todo[] {
    return this.task?.todos?.sort((a, b) => a.rank - b.rank) || [];
  }
}
